import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Divider, TextField, Button, Stack, CircularProgress
} from '@mui/material';
import { IconButton } from '../../components/icon-button/IconButton.tsx';
import { Confirm, List, useNotify, useRefresh } from 'react-admin';
import { useTranslation } from 'react-i18next';
import SupportService from '../../services/support/supportService.ts';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TwoWheelerOutlinedIcon from '@mui/icons-material/TwoWheelerOutlined';
import { useSupportNotification } from '../../context/SupportNotificationContext.tsx';
import * as Sentry from '@sentry/react';

const supportService = new SupportService();

type MessageDTO = {
    messageSid: string;
    author: string;
    body: string;
    timestamp: string;
};

type ConversationDTO = {
    conversationSid: string;
    friendlyName: string;
    state: string;
    createdAt: string;
};

const stateColor = (state: string) => {
    switch (state) {
        case 'active': return 'var(--color-primary, #4db6ac)';
        case 'inactive': return '#f0ad4e';
        case 'closed': return '#8c8c8c';
        default: return '#8c8c8c';
    }
};

const SupportActions: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Box sx={{ pt: 'var(--space-72)', pb: 'var(--space-32)', flex: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ pl: 'var(--space-24)', fontWeight: 700, fontSize: 'var(--text-xxl)' }}>
                {t('resources.support', 'Support')}
            </Typography>
        </Box>
    );
};

export default function SupportScreen(): JSX.Element {
    return (
        <List
            resource="support"
            actions={<SupportActions />}
            queryOptions={{ enabled: false }}
            pagination={false}
            sx={{
                '& .RaList-main': { px: 4 },
                '& .RaList-content': { border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6)' },
            }}
        >
            <SupportLayout />
        </List>
    );
}

const SupportLayout: React.FC = () => {
    const [conversations, setConversations] = useState<ConversationDTO[]>([]);
    const [selectedSid, setSelectedSid] = useState<string | null>(null);
    const [messages, setMessages] = useState<MessageDTO[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [loadingConvs, setLoadingConvs] = useState(true);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [toDelete, setToDelete] = React.useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const knownSidsRef = useRef<Set<string>>(new Set());
    const { t } = useTranslation();
    const { markAllRead } = useSupportNotification();

    useEffect(() => {
        markAllRead();
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await supportService.getConversations();
                setConversations(data);
                if (data.length > 0 && !selectedSid) {
                    setSelectedSid(data[0].conversationSid);
                }
            } catch (e) {
                console.error('Failed to load conversations:', e);
                Sentry.captureException(e);
            } finally {
                setLoadingConvs(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (!selectedSid) return;

        const loadMessages = async () => {
            setLoadingMsgs(true);
            try {
                const data = await supportService.getMessages(selectedSid);
                setMessages(data);
                knownSidsRef.current = new Set(data.map((m: MessageDTO) => m.messageSid));
            } catch (e) {
                console.error('Failed to load messages:', e);
                Sentry.captureException(e);
            } finally {
                setLoadingMsgs(false);
            }
        };
        loadMessages();

        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
            try {
                const data = await supportService.getMessages(selectedSid);
                if (data.length !== knownSidsRef.current.size || data.some((m: MessageDTO) => !knownSidsRef.current.has(m.messageSid))) {
                    setMessages(data);
                    knownSidsRef.current = new Set(data.map((m: MessageDTO) => m.messageSid));
                }
            } catch (e) {
                console.warn('Polling error:', e);
                Sentry.captureException(e);
            }
        }, 3000);

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [selectedSid]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    const handleSend = useCallback(async () => {
        if (!inputValue.trim() || !selectedSid) return;
        const text = inputValue.trim();
        setInputValue('');
        setSending(true);

        const tempId = `temp-${Date.now()}`;
        const tempMsg: MessageDTO = {
            messageSid: tempId,
            author: 'Support Team',
            body: text,
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, tempMsg]);
        knownSidsRef.current.add(tempId);

        try {
            await supportService.sendMessage(selectedSid, text);
        } catch (e) {
            console.error('Failed to send message:', e);
            Sentry.captureException(e);
            setMessages(prev => prev.filter(m => m.messageSid !== tempId));
            knownSidsRef.current.delete(tempId);
        } finally {
            setSending(false);
        }
    }, [inputValue, selectedSid]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const notify = useNotify();
    const refresh = useRefresh();
    const selected = conversations.find(c => c.conversationSid === selectedSid);

    const getRoleIcon = (roles: string) => {
        const role = JSON.parse(roles).role;
        switch (role) {
            case '[CUSTOMER]': return <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
            case '[VENDOR]': return <StorefrontOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
            case '[COURIER]': return <TwoWheelerOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
            default: return null;
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', height: 700 }}>
                <Box sx={{ width: 340, minWidth: 340, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
                    <Box sx={{ px: 2, py: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Conversations</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {loadingConvs ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : conversations.length === 0 ? (
                            <Typography sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: '13px' }}>
                                No conversations yet
                            </Typography>
                        ) : (
                            conversations.map(conv => {
                                console.log("conv: " + JSON.stringify(conv));
                                const isActive = conv.conversationSid === selectedSid;
                                return (
                                    <Box
                                        key={conv.conversationSid}
                                        onClick={() => setSelectedSid(conv.conversationSid)}
                                        sx={{
                                            px: 2, py: 1.5, cursor: 'pointer',
                                            backgroundColor: isActive ? 'rgba(77, 182, 172, 0.08)' : 'transparent',
                                            borderLeft: isActive ? '3px solid var(--color-primary, #4db6ac)' : '3px solid transparent',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                                            borderBottom: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                            {/* <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ mr: 1}}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                                                    {getRoleIcon(conv.roles)}
                                                </Typography>
                                            </Stack> */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                                                        {conv.friendlyName || conv.conversationSid}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary', ml: 1, mr: 2, whiteSpace: 'nowrap' }}>
                                                {conv.createdAt ? new Date(conv.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                            </Typography>
                                            <IconButton icon={<DeleteOutlineIcon />} color="var(--color-text-primary)" sx={{ minWidth: 0 }} onClick={() => {
                                                setConfirmOpen(true);
                                                setToDelete(conv.conversationSid);
                                            }}
                                            />
                                        </Stack>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fafafa' }}>
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>
                            {selected ? (selected.friendlyName || 'Chat') : 'Select a conversation'}
                        </Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                        {loadingMsgs ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                {messages.map(msg => {
                                    const isAgent = msg.author === 'Support Team';
                                    return (
                                        <Box key={msg.messageSid} sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
                                            <Box sx={{
                                                maxWidth: '60%',
                                                backgroundColor: isAgent ? '#e0e0e0' : 'var(--color-primary, #4db6ac)',
                                                color: isAgent ? '#000' : '#fff',
                                                borderRadius: isAgent ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                                px: 2, py: 1.2,
                                            }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '12px', mb: 0.3 }}>
                                                    {isAgent ? 'Support Agent' : msg.author}
                                                </Typography>
                                                <Typography sx={{ fontSize: '13px', lineHeight: 1.5 }}>
                                                    {msg.body}
                                                </Typography>
                                                <Typography sx={{ fontSize: '10px', color: isAgent ? 'text.secondary' : 'rgba(255,255,255,0.7)', mt: 0.5, textAlign: 'right' }}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField
                            fullWidth
                            placeholder="Type a message..."
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            multiline
                            maxRows={3}
                            size="small"
                            disabled={!selectedSid || sending}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSend}
                            disabled={!inputValue.trim() || !selectedSid || sending}
                            sx={{
                                backgroundColor: 'var(--color-primary, #4db6ac)', color: '#fff',
                                textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3,
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: 'var(--color-primary, #3d9e94)', boxShadow: 'none' },
                            }}
                        >
                            {sending ? <CircularProgress size={20} color="inherit" /> : 'Send'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Confirm
                isOpen={confirmOpen}
                title={t('support.dialog.confirmDeletionTitle',)}
                content={t('support.dialog.confirmDeletionBody')}
                confirm={t('common.delete')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    try {
                        await supportService.deleteConversation(toDelete!);
                        notify(t('common.saved'), { type: 'success' });
                        setConfirmOpen(false);
                        setToDelete(null);
                    } catch (error: any) {
                        notify(error?.response?.data?.error, { type: 'error' });
                        console.error(error?.response?.data?.error);
                        Sentry.captureException(error);
                        setConfirmOpen(false);
                        setToDelete(null);
                        return;
                    } finally {
                        window.location.reload();
                    }
                }}
                onClose={() => {
                    setConfirmOpen(false);
                    setToDelete(null);
                }}
            />
        </>
    );
};